'use es6';

import tinymce from 'tinymce';
import debounce from 'react-utils/debounce';
import { SpacingIconTypes } from 'rich-text-lib/constants/spacing';
import TableInsertPopoverBody from 'rich-text-lib/components/table/TableInsertPopoverBody';
import TablePopoverManagerV2 from '../components/TablePopoverManagerV2';
import TableCellPopoverManager from '../components/TableCellPopoverManager';
import { getTableContextualToolbar, getTableCellContextualToolbar } from '../constants/contextualToolbar';
import { getSelectedCells, getSelectedTableNode, isTableNode } from '../utils/table';
import { TABLE_SETTINGS, hstableConfig } from '../constants/hstable2';
import { EDIT_TABLE } from '../constants/usageTracking';
import { i18nWithNameSpace } from '../utils/i18n';
import { trackInteraction } from '../utils/usageTracking';
import { getPluginSetting } from '../utils';
import { CANVAS_TABLE } from '../constants/toolbar';
import { isEditableImage } from '../utils/image';
import { isEditableVideo } from '../utils/video';
import { isEditableEmbed } from '../utils/embed';
import { nodeIsCtaPlaceholder } from '../utils/cta';
import { nodeIsIconElement } from '../utils/icon';
import { isAnchorNode } from '../utils/anchor';
import { isEditableToken } from '../utils/token';
import { getTableAttributes, getTableCellAttributes } from './util';
import { getCloneTableHandler, getDeleteTableHandler, getInsertTableHandler } from './handlers';
import { jsx as _jsx } from "react/jsx-runtime";
const i18nKey = i18nWithNameSpace('TinymcePlugins.hstableV2');
const i18nCellKey = i18nWithNameSpace('TinymcePlugins.hstableV2.cell');
const debouncedTrackInteraction = debounce(trackInteraction, 400);
export function addControls(editor, pluginName) {
  const {
    [hstableConfig.HIDE_CELL_COLOR_OPTIONS]: hideCellColorOptions,
    [hstableConfig.DISABLE_SPACING]: disableMarginControls
  } = getPluginSetting(editor, TABLE_SETTINGS, {});
  const useTableContextualToolbar = editor.getParam('useTableContextualToolbar');
  const handleEditTableCommand = cmd => {
    editor.execCommand(cmd);
    debouncedTrackInteraction(editor, 'tinymceEditTable', EDIT_TABLE, {
      version: useTableContextualToolbar ? 'contextualToolbar' : 'v1',
      command: cmd
    });
  };
  const trackEditTableStyle = editType => {
    debouncedTrackInteraction(editor, 'tinymceEditTable', EDIT_TABLE, {
      version: useTableContextualToolbar ? 'contextualToolbar' : 'v2',
      editType
    });
  };
  tinymce.canvas.addMenuItem(editor, CANVAS_TABLE, {
    text: i18nKey('menuItem'),
    child: () => /*#__PURE__*/_jsx(TableInsertPopoverBody, {
      onInsertTable: getInsertTableHandler(editor)
    })
  });

  /**
   * TABLE popover handlers
   */

  const isWithinActiveTableNodeHandler = ({
    element
  }) => {
    if (!element) return false;
    return getSelectedTableNode(editor, element);
  };
  tinymce.canvas.addNodeChangeHandler(editor, {
    eventKey: pluginName,
    isActiveNodeHandler: isWithinActiveTableNodeHandler
  });
  tinymce.canvas.addPopover(editor, pluginName, {
    renderPopover(renderProps) {
      return /*#__PURE__*/_jsx(TablePopoverManagerV2, Object.assign({}, renderProps, {
        editor: editor,
        eventKey: pluginName,
        onDeleteTable: getDeleteTableHandler(editor),
        onCloneTable: getCloneTableHandler(editor)
      }));
    }
  });
  const inhibitKeyDownOnActiveTable = evt => {
    const {
      key
    } = evt;
    if (key !== 'Backspace') {
      // Prevent non-backspace keys from deleting the table when its selected
      evt.preventDefault();
    }
    trackEditTableStyle('deleteTable');
  };
  const isActiveTableNodeHandler = ({
    element
  }) => {
    if (!element) return false;
    if (isTableNode(element)) {
      editor.on('KeyDown', inhibitKeyDownOnActiveTable);
      return element;
    }
    editor.off('KeyDown', inhibitKeyDownOnActiveTable);
    return false;
  };
  const getActiveTableNodeAttributes = node => {
    return Object.assign({}, getTableAttributes(editor, node), {
      borderColorTooltip: i18nKey('borderColorTooltip'),
      allowUnitDimensions: true,
      lockedAspectRatio: false,
      marginIconType: SpacingIconTypes.TABLE
    });
  };
  tinymce.canvas.addNodeChangeHandler(editor, {
    eventKey: `${pluginName}-toolbar`,
    isActiveNodeHandler: isActiveTableNodeHandler,
    getActiveNodeAttributes: getActiveTableNodeAttributes,
    contextualToolbar: getTableContextualToolbar({
      showMarginControls: !disableMarginControls
    })
  });

  /**
   * TABLE CELL popover handlers
   */

  const isActiveTableCellNodeHandler = ({
    element
  }) => {
    if (!element) return false;
    if (isTableNode(element)) return false;
    const isWithinTable = getSelectedTableNode(editor);
    if (!isWithinTable) return false;
    const selectionIsImage = isEditableImage(editor, element);
    const selectionIsVideo = isEditableVideo(editor, element);
    const selectionIsEmbed = isEditableEmbed(editor, element);
    const selectionIsCta = nodeIsCtaPlaceholder(editor, element);
    const selectionIsIcon = nodeIsIconElement(editor, element);
    const selectionIsToken = isEditableToken(editor, element);
    const selectionIsAnchor = isAnchorNode(element);
    const selectionHasOwnPopover = selectionIsImage || selectionIsVideo || selectionIsEmbed || selectionIsCta || selectionIsIcon || selectionIsToken || selectionIsAnchor;
    if (selectionHasOwnPopover) return false;
    const selectedTableCells = getSelectedCells(editor, element);
    return selectedTableCells.length ? selectedTableCells : false;
  };
  const getActiveTableCellAttributes = tableCells => {
    return Object.assign({}, getTableCellAttributes(editor, tableCells), {
      fillColorTooltip: i18nCellKey('backgroundColorTooltip'),
      borderColorTooltip: i18nCellKey('borderColorTooltip'),
      useCellVerticalAlignment: true
    });
  };
  const tableCellEventKey = `${pluginName}-cell`;
  tinymce.canvas.addNodeChangeHandler(editor, {
    eventKey: tableCellEventKey,
    isActiveNodeHandler: isActiveTableCellNodeHandler,
    getActiveNodeAttributes: getActiveTableCellAttributes,
    contextualToolbar: getTableCellContextualToolbar({
      canvasToolbar: editor.getParam('canvasToolbar'),
      showCellColorControls: !hideCellColorOptions
    })
  });
  const getEditTableHandler = cmd => () => {
    handleEditTableCommand(cmd);
  };
  tinymce.canvas.addPopover(editor, tableCellEventKey, {
    renderPopover(renderProps) {
      return /*#__PURE__*/_jsx(TableCellPopoverManager, Object.assign({}, renderProps, {
        editor: editor,
        eventKey: tableCellEventKey,
        onMergeCells: getEditTableHandler('mceTableMergeCells'),
        onSplitCells: getEditTableHandler('mceTableSplitCells'),
        onInsertRowAbove: getEditTableHandler('mceTableInsertRowBefore'),
        onInsertRowBelow: getEditTableHandler('mceTableInsertRowAfter'),
        onInsertColumnLeft: getEditTableHandler('mceTableInsertColBefore'),
        onInsertColumnRight: getEditTableHandler('mceTableInsertColAfter'),
        onDeleteRow: getEditTableHandler('mceTableDeleteRow'),
        onDeleteColumn: getEditTableHandler('mceTableDeleteCol')
      }));
    }
  });
}